import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, CLAVE_AGENTE } from '../config/api';

export interface User {
  id: number;
  nombre: string;
  email: string;
  rol: string;
}

export interface Suscripcion {
  estado: string;           // trial | activa | gracia | vencida | cancelada
  diasRestantes: number;
  plan: string;
  planNombre?: string;
  vigente?: boolean;
  fin?: string | null;
  cancelarAlFin?: boolean;
  cobroAutomatico?: boolean;
}

interface AuthContextType {
  user: User | null;
  suscripcion: Suscripcion | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: any) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [suscripcion, setSuscripcion] = useState<Suscripcion | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('respondo_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
      setSuscripcion(data.suscripcion);
    } catch {
      localStorage.removeItem('respondo_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email: string, password: string) => {
    localStorage.removeItem(CLAVE_AGENTE);
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('respondo_token', data.token);
    await refresh();
  };

  const register = async (payload: any) => {
    const { data } = await api.post('/auth/register', payload);
    localStorage.setItem('respondo_token', data.token);
    await refresh();
  };

  const logout = () => {
    api.post('/auth/logout').catch(() => {});
    localStorage.removeItem('respondo_token');
    localStorage.removeItem(CLAVE_AGENTE);
    setUser(null);
    setSuscripcion(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, suscripcion, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};
